/**
 * Browser-side bootstrap for Bugsnag. `@bugsnag/js` is not needed to render the
 * page, so it stays out of the critical bundle and is loaded on demand: after
 * `load` by _app, or earlier by _error when Next renders it for an error.
 *
 * Until it has started, this module buffers what Bugsnag's own global handlers
 * would catch (uncaught errors, unhandled rejections) and replays them once it
 * has. Both callers go through the same memoised start, so the buffer is
 * drained exactly once and nothing is reported twice.
 *
 * On the server this is a no-op; _app starts Bugsnag there synchronously.
 */
import type { PendingNotification } from './bugsnag';

const isBrowser = typeof window !== 'undefined';

// Bounded, so a page whose reporter never loads (a stale tab after a deploy,
// say) cannot grow it without limit; the first errors are the telling ones.
const MAX_EARLY_ERRORS = 20;
const earlyErrors: unknown[] = [];
const buffer = (error: unknown) => {
  if (earlyErrors.length < MAX_EARLY_ERRORS) earlyErrors.push(error);
};
const bufferError = (event: ErrorEvent) => {
  buffer(event.error ?? event.message);
};
const bufferRejection = (event: PromiseRejectionEvent) => {
  buffer(event.reason);
};

if (isBrowser) {
  window.addEventListener('error', bufferError);
  window.addEventListener('unhandledrejection', bufferRejection);
}

// Explicit notifications (from _error) made while the reporter wasn't running,
// delivered after a successful start; bounded like the event buffer.
const pendingNotifications: PendingNotification[] = [];

/** Queues a notification for delivery once Bugsnag has started. */
export function queueNotification(notification: PendingNotification): void {
  if (pendingNotifications.length < MAX_EARLY_ERRORS) pendingNotifications.push(notification);
}

let starting: Promise<boolean> | undefined;

/**
 * Loads and starts Bugsnag once, replaying buffered early errors, and resolves
 * to whether it is running. Concurrent callers share one attempt; a failed
 * attempt (the chunk didn't load, say) is forgotten so the next caller retries,
 * with the buffer still intact. Nothing can report that failure itself.
 */
export function ensureBugsnagStarted(): Promise<boolean> {
  if (!isBrowser) return Promise.resolve(true);
  starting ??= import('./bugsnag')
    .then(({ start, notifyEarlyErrors, notifyPending }) => {
      // Start first: if it throws, the buffers keep collecting for the retry.
      start();
      window.removeEventListener('error', bufferError);
      window.removeEventListener('unhandledrejection', bufferRejection);
      notifyEarlyErrors(earlyErrors.splice(0));
      notifyPending(pendingNotifications.splice(0));
      return true;
    })
    .catch(() => {
      starting = undefined;
      return false;
    });
  return starting;
}

const RETRY_DELAY_MS = 30_000;

/**
 * Starts Bugsnag once the page has loaded, so it never competes with rendering,
 * retrying once if the first attempt fails (a transient network error).
 */
export function startBugsnagAfterLoad(): void {
  if (!isBrowser) return;
  const start = () => {
    void ensureBugsnagStarted().then((started) => {
      if (!started) window.setTimeout(() => void ensureBugsnagStarted(), RETRY_DELAY_MS);
    });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
